import { Card } from '@/types/Card';
import {
  CARD_KEYS,
  CHANNEL_KEYS,
  CHANNEL_NAMES,
  BANK_KEYS,
  BANK_NAMES,
} from '../../constants/keys';
import { FeatureType } from '@/types/Feature';
import { CardLinkMapping, BankLinkMapping } from '../cardLinks';

/**
 * Map of bank keys to their source URLs
 * This allows us to easily update bank information when they change their websites
 * The key is the bank key from BANK_KEYS
 */
export const bankLinkMap: Record<string, BankLinkMapping> = {
  [BANK_KEYS.FUBON]: {
    sourceUrl: 'https://www.fubon.com/banking/personal/credit_card/all_card/all_card.htm',
    lastUpdated: '2025-05-04',
  },
};

/**
 * Map of card keys to their source URLs
 * This allows us to easily update card information when banks change their offerings
 * The key is the card key from CARD_KEYS
 */
export const cardLinkMap: Record<string, CardLinkMapping> = {
  // Fubon Bank cards
  [CARD_KEYS.FUBON_J]: {
    sourceUrl: 'https://www.fubon.com/banking/event/hot/omiyagecard/',
    applyUrl: 'https://www.fubon.com/banking/personal/credit_card/apply/index.html',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.FUBON_DIGITAL]: {
    sourceUrl:
      'https://www.fubon.com/banking/personal/credit_card/all_card/digitallife/digitallife.htm',
    applyUrl: 'https://www.fubon.com/banking/personal/credit_card/apply/index.html',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.FUBON_MOMO]: {
    sourceUrl: 'https://www.fubon.com/banking/personal/credit_card/all_card/momo/momo.htm',
    applyUrl: 'https://www.fubon.com/banking/personal/credit_card/all_card/momo/momo.htm',
    lastUpdated: '2025-05-10',
  },
  [CARD_KEYS.FUBON_LIFESTYLE]: {
    sourceUrl:
      'https://www.fubon.com/banking/personal/credit_card/all_card/lifestyle/lifestyle.htm',
    applyUrl: 'https://www.fubon.com/banking/personal/credit_card/all_card/lifestyle/lifestyle.htm',
    lastUpdated: '2025-05-10',
  },
  [CARD_KEYS.FUBON_COSTCO]: {
    sourceUrl: 'https://www.fubon.com/banking/personal/credit_card/all_card/costco/costco.htm',
    applyUrl: 'https://www.fubon.com/banking/personal/credit_card/all_card/costco/costco.htm',
    lastUpdated: '2025-05-10',
  },
  [CARD_KEYS.FUBON_INSURANCE]: {
    sourceUrl:
      'https://www.fubon.com/banking/personal/credit_card/all_card/insurance/insurance.htm',
    applyUrl: 'https://www.fubon.com/banking/personal/credit_card/all_card/insurance/insurance.htm',
    lastUpdated: '2025-05-10',
  },
};

export const fubonCards: { [key: string]: Card } = {
  [CARD_KEYS.FUBON_DIGITAL]: {
    name: '富邦數位生活卡',
    summary: '網購最高10%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.FUBON],
    payment_network: 'Mastercard',
    picture: 'logo_fubon-digital.png',
    key: 'fubon-digital',
    link: cardLinkMap[CARD_KEYS.FUBON_DIGITAL].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.FUBON_DIGITAL].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '10% 現金',
        max_feedback: '每月最高回饋500元',
        simplified_condition: '指定電商平台',
        condition: '指定電商平台消費享10%現金回饋，每月最高回饋500元',
        channel: '網購',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '5% 現金',
        max_feedback: '每月最高回饋300元',
        simplified_condition: '一般網購',
        condition: '一般網購消費享5%現金回饋，每月最高回饋300元',
        channel: '網購',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1% 現金',
        max_feedback: '回饋無上限',
        simplified_condition: '一般消費',
        condition: '一般消費享1%現金回饋，無上限',
        channel: '國內一般消費',
      },
    ],
    domestic_feedback: '1%',
    international_feedback: '1%',
    specific_channel_feedback: '10%',
    start_date: '2025-01-01',
    end_date: '2025-08-31',
  },
  [CARD_KEYS.FUBON_J]: {
    name: '台北富邦J卡',
    summary: '日韓泰旅遊最高8%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.FUBON],
    payment_network: 'JCB',
    picture: 'logo_fubon-j.webp',
    key: 'fubon-j',
    link: cardLinkMap[CARD_KEYS.FUBON_J].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.FUBON_J].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '6% 現金',
        max_feedback: '每季最高回饋600元',
        simplified_condition: '日韓實體消費',
        condition: '日韓實體消費享3%無上限+加碼3%回饋，每季最高回饋600元，需登錄',
        channel: '海外',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '6% 現金',
        max_feedback: '每季最高回饋600元',
        simplified_condition: '泰國實體消費',
        condition: '泰國實體消費享1%無上限+加碼5%回饋，每季最高回饋600元，需登錄',
        channel: '海外',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '10% 現金',
        max_feedback: '每季最高回饋200元',
        simplified_condition: '日韓指定便利商店',
        condition:
          '日本7-11、LAWSON、FAMILY MART及韓國emart24、GS25、CU便利商店消費享10%回饋，每季最高回饋200元，需登錄',
        channel: '海外',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '6% 現金',
        max_feedback: '每月最高回饋500元',
        simplified_condition: '指定航空、旅行社',
        condition:
          '中華航空、長榮航空、星宇航空、台灣虎航及易遊網、可樂旅遊、雄獅旅遊、東南旅遊、山富旅遊消費享6%回饋，每月最高回饋500元，需登錄',
        channel: '旅遊',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '6% 現金',
        max_feedback: '每月最高回饋100元',
        simplified_condition: 'foodpanda',
        condition: '透過foodpanda官網/APP消費享6%回饋，每月最高回饋100元，需登錄',
        channel: '外送',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '6% 現金',
        max_feedback: '每月回饋上限依活動而定',
        simplified_condition: '指定速食店、手搖飲',
        condition: '指定速食店、手搖飲消費享6%回饋，每月回饋上限依活動而定，需登錄',
        channel: '餐飲',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1% 現金',
        max_feedback: '回饋無上限',
        simplified_condition: '一般消費',
        condition: '一般消費享1%現金回饋，無上限',
        channel: '國內一般消費',
      },
    ],
    domestic_feedback: '1%',
    international_feedback: '3%',
    specific_channel_feedback: '8%',
    start_date: '2025-01-01',
    end_date: '2025-06-30',
  },
  [CARD_KEYS.FUBON_MOMO]: {
    name: '富邦momo卡',
    summary: 'momo通路最高3%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.FUBON],
    payment_network: 'Mastercard',
    picture: 'logo_fubon-momo.webp',
    key: 'fubon-momo',
    link: cardLinkMap[CARD_KEYS.FUBON_MOMO].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.FUBON_MOMO].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% momo幣',
        max_feedback: '每期帳單回饋上限1,000 momo幣',
        simplified_condition: 'momo通路消費',
        condition:
          '於momo購物網、看看買、型錄、mo店+消費享3% momo幣回饋，每期帳單回饋上限1,000 momo幣',
        channel: '網購',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1% momo幣',
        max_feedback: '回饋無上限',
        simplified_condition: '一般消費',
        condition: '一般消費享1% momo幣回饋，無上限',
        channel: '國內一般消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1% momo幣',
        max_feedback: '回饋無上限',
        simplified_condition: '海外消費',
        condition: '海外消費享1% momo幣回饋，無上限',
        channel: '海外',
      },
      {
        type: FeatureType.BENEFIT,
        feedback: '前3期免息',
        max_feedback: '12期以上分期',
        simplified_condition: '海外消費',
        condition: '海外消費單筆分期享12期以上前三期免息優惠',
        channel: '海外',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '0.5% momo幣',
        max_feedback: '回饋無上限',
        simplified_condition: '保費',
        condition: '保費消費享0.5% momo幣回饋或最高12期分期0利率二選一',
        channel: '保險',
      },
    ],
    domestic_feedback: '1%',
    international_feedback: '1%',
    specific_channel_feedback: '3%',
    start_date: '2023-12-01',
    end_date: '2025-06-30',
  },
  [CARD_KEYS.FUBON_LIFESTYLE]: {
    name: '富邦富利生活卡',
    summary: '8大生活消費5倍紅利',
    issuing_bank: BANK_NAMES[BANK_KEYS.FUBON],
    payment_network: 'VISA',
    picture: 'logo_fubon-lifestyle.webp',
    key: 'fubon-lifestyle',
    link: cardLinkMap[CARD_KEYS.FUBON_LIFESTYLE].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.FUBON_LIFESTYLE].applyUrl,
    features: [
      {
        type: FeatureType.BENEFIT,
        feedback: '5倍紅利',
        max_feedback: '回饋無上限',
        simplified_condition: '8大生活消費通路',
        condition:
          '於百貨、量販、超市、餐廳、加油、書店、旅行社、航空公司等8大通路消費享5倍紅利回饋(NT$20=5點)，回饋無上限',
        channel: '百貨',
      },
      {
        type: FeatureType.INSTALLMENT,
        feedback: '前3期免息',
        max_feedback: '海外消費單筆分期',
        simplified_condition: '海外消費',
        condition: '海外消費單筆分期享前3期免息優惠',
        channel: '海外',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '300元刷卡金',
        max_feedback: '指定通路分期',
        simplified_condition: '旅行社、免稅店',
        condition: '指定旅行社、免稅店分期加碼送300元刷卡金',
        channel: '旅遊',
      },
      {
        type: FeatureType.DISCOUNT,
        feedback: '最高220元折抵',
        max_feedback: '外送/生鮮雜貨',
        simplified_condition: 'Foodpanda',
        condition: 'Foodpanda外送/生鮮雜貨最高折抵220元',
        channel: '外送',
      },
    ],
    domestic_feedback: '1倍紅利',
    international_feedback: '1倍紅利',
    specific_channel_feedback: '5倍紅利',
    start_date: '2023-01-01',
    end_date: '2025-06-30',
  },
  [CARD_KEYS.FUBON_COSTCO]: {
    name: '富邦Costco聯名卡',
    summary: 'Costco店內2%好多金回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.FUBON],
    payment_network: 'Mastercard',
    picture: 'logo_fubon-costco.jpg',
    key: 'fubon-costco',
    link: cardLinkMap[CARD_KEYS.FUBON_COSTCO].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.FUBON_COSTCO].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '2% 好多金',
        max_feedback: '回饋無上限',
        simplified_condition: 'Costco店內消費',
        condition: '於台灣Costco店內消費享2%好多金回饋無上限',
        channel: '量販',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% 好多金',
        max_feedback: '回饋無上限',
        simplified_condition: 'Costco線上購物',
        condition: '於Costco線上購物享3%好多金回饋無上限',
        channel: '網購',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% 好多金',
        max_feedback: '回饋無上限',
        simplified_condition: 'Costco自助加油站',
        condition: '於Costco自助加油站消費享3%好多金回饋無上限',
        channel: '加油',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1% 好多金',
        max_feedback: '回饋無上限',
        simplified_condition: '店外一般消費',
        condition: '店外一般消費享1%好多金回饋無上限',
        channel: '國內一般消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '0.5% 好多金',
        max_feedback: '回饋無上限',
        simplified_condition: '保費消費',
        condition: '保費消費享0.5%好多金回饋無上限或最高12期分期0利率二選一',
        channel: '保險',
      },
    ],
    domestic_feedback: '1%',
    international_feedback: '1%',
    specific_channel_feedback: '3%',
    start_date: '2023-08-08',
    end_date: '2026-12-31',
  },
  [CARD_KEYS.FUBON_INSURANCE]: {
    name: '富邦鑽保卡',
    summary: '保費最高12期分期0利率',
    issuing_bank: BANK_NAMES[BANK_KEYS.FUBON],
    payment_network: 'VISA',
    picture: 'logo_fubon-insurance.jpg',
    key: 'fubon-insurance',
    link: cardLinkMap[CARD_KEYS.FUBON_INSURANCE].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.FUBON_INSURANCE].applyUrl,
    features: [
      {
        type: FeatureType.DISCOUNT,
        feedback: '1% 折扣',
        max_feedback: '富邦人壽保費',
        simplified_condition: '富邦人壽保費',
        condition: '扣繳富邦人壽保費享1%折扣，部分商品無法享有優惠折扣',
        channel: '保險',
      },
      {
        type: FeatureType.BENEFIT,
        feedback: '最高12期分期0利率',
        max_feedback: '單筆5萬元以上',
        simplified_condition: '保費交易',
        condition:
          '保費交易可享最高12期分期0利率或0.5%現金回饋二選一，114/4/1起設定9期或12期自動分期優惠，單筆保費金額需達新台幣5萬元',
        channel: '保險',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '0.7% 現金回饋',
        max_feedback: '回饋無上限',
        simplified_condition: '一般消費',
        condition: '一般消費享0.7%現金回饋，回饋無上限',
        channel: '國內一般消費',
      },
      {
        type: FeatureType.INSTALLMENT,
        feedback: '前3期免息',
        max_feedback: '海外消費單筆分期',
        simplified_condition: '海外消費',
        condition: '海外消費單筆分期享前3期免息優惠',
        channel: '海外',
      },
    ],
    domestic_feedback: '0.7%',
    international_feedback: '0.7%',
    specific_channel_feedback: '1%',
    start_date: '2024-01-01',
    end_date: '2025-06-30',
  },
};
